import React from 'react';

import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import { ValidationIssue } from '../../hooks/useContextMenuActions';

type SoScheduleWarningDialogProps = {
  visible: boolean;
  onHide: () => void;
  onAccept: () => void;
  validationIssues: ValidationIssue[];
  workflowName?: string;
};

export const SoScheduleWarningDialog: React.FC<
  SoScheduleWarningDialogProps
> = ({
  visible,
  onHide,
  onAccept,
  validationIssues,
  workflowName = 'SO Schedule',
}) => {
  const { t } = useTranslation();

  const hasLockedIssues = validationIssues.some((issue) => issue.isLocked);

  const renderFooter = () => {
    if (hasLockedIssues) {
      return (
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
            onClick={onHide}
          >
            <i className="pi pi-times mr-2"></i>
            {t('common.close')}
          </button>
        </div>
      );
    }

    return (
      <div className="flex justify-end gap-3">
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
          onClick={onHide}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
          onClick={onAccept}
        >
          {t('common.proceedAnyway')}
        </button>
      </div>
    );
  };

  return (
    <Dialog
      header={`${workflowName} Warning`}
      visible={visible}
      onHide={onHide}
      footer={renderFooter()}
      style={{ width: '50vw' }}
      className="so-schedule-warning-dialog"
    >
      <div className="space-y-6">
        <div
          className={`flex items-start rounded-lg border p-4 ${
            hasLockedIssues
              ? 'border-red-200 bg-red-50'
              : 'border-yellow-200 bg-yellow-50'
          }`}
        >
          <div>
            <div className="flex flex-row items-center justify-start">
              <i
                className={`pi ${hasLockedIssues ? 'pi-lock' : 'pi-exclamation-triangle'} mt-0.5 mr-3 text-lg ${hasLockedIssues ? 'text-red-500' : 'text-yellow-500'}`}
              ></i>
              <h3
                className={`mb-2 text-sm font-medium ${hasLockedIssues ? 'text-red-800' : 'text-yellow-800'}`}
              >
                {hasLockedIssues
                  ? 'SO Schedule Blocked'
                  : t('donorScheduling.soScheduleValidationWarning')}
              </h3>
            </div>
            <p
              className={`text-sm ${hasLockedIssues ? 'text-red-700' : 'text-yellow-700'}`}
            >
              {hasLockedIssues
                ? 'The following issues prevent SO Schedule from running. These must be resolved before proceeding.'
                : t('donorScheduling.soScheduleValidationWarningDetail')}
            </p>
          </div>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-medium text-gray-900">
            {t('donorScheduling.donorSchedulesWithIssues')}
          </h4>
          <div className="max-h-64 space-y-3 overflow-y-auto">
            {validationIssues.map((issue) => (
              <div
                key={issue.donorScheduleId}
                className={`rounded-md border p-3 ${
                  issue.isLocked
                    ? 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div
                  className={`mb-2 text-sm font-medium ${issue.isLocked ? 'text-red-900' : 'text-gray-900'}`}
                >
                  {issue.donorScheduleName}
                  {issue.isLocked && (
                    <i className="pi pi-lock ml-2 text-xs text-red-600"></i>
                  )}
                </div>
                <ul className="space-y-1">
                  {issue.issues.map((problem, problemIndex) => (
                    <li
                      key={problemIndex}
                      className={`flex items-center text-sm ${issue.isLocked ? 'text-red-700' : 'text-gray-600'}`}
                    >
                      <i
                        className={`pi pi-circle-fill mr-2 text-[5px] ${issue.isLocked ? 'text-red-600' : 'text-gray-600'}`}
                      ></i>
                      {problem}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
